import { getCustomerServiceUserProfile } from '../../../api/customerServiceUserProfileApi'
import { getDateTime } from '../../../helpers/utils'
import translate from '../../../i18n'
import styles from './customer-service-user-profile.module.less'
import { CustomerServiceUserProfile, CustomerServiceUserProfileParams } from './share-types'
import { Card, Descriptions, Result, Skeleton, Tag } from 'antd'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

type PageState = 'loading' | 'invalidParameters' | 'notFound' | 'loadError' | 'ready'

const displayValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '-' : value

const userTypeTag = (userType: string) => {
  const tags: Record<string, { color: string; label: string }> = {
    CORPORATE_USER: { color: 'blue', label: translate('customerServiceUserProfile.corporate') },
    NEW_USER: { color: 'green', label: translate('customerServiceUserProfile.new') },
    OLD_USER: { color: 'default', label: translate('customerServiceUserProfile.existing') },
  }
  const tag = tags[userType] ?? { color: 'default', label: userType }

  return <Tag color={tag.color}>{tag.label}</Tag>
}

const gender = (value: number | null) => {
  if (value === 1) {
    return translate('customerServiceUserProfile.male')
  }
  if (value === 2) {
    return translate('customerServiceUserProfile.female')
  }

  return '-'
}

const CustomerServiceUserProfilePage = () => {
  const { search } = useLocation()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [profile, setProfile] = useState<CustomerServiceUserProfile>()

  useEffect(() => {
    const queryParameters = new URLSearchParams(search)
    const parameters: CustomerServiceUserProfileParams = {
      appid: queryParameters.get('appid')?.trim() ?? '',
      openid: queryParameters.get('openid')?.trim() ?? '',
      kefuid: queryParameters.get('kefuid')?.trim() ?? '',
    }

    if (!parameters.appid || !parameters.openid || !parameters.kefuid) {
      setPageState('invalidParameters')
      return
    }

    let isUnmounted = false
    setPageState('loading')
    getCustomerServiceUserProfile(parameters)
      .then(response => {
        if (!isUnmounted) {
          setProfile(response)
          setPageState('ready')
        }
      })
      .catch(error => {
        if (!isUnmounted) {
          const statusCode = error?.statusCode ?? Number(error?.code)
          setPageState(
            statusCode === 400
              ? 'invalidParameters'
              : statusCode === 404
                ? 'notFound'
                : 'loadError',
          )
        }
      })

    return () => {
      isUnmounted = true
    }
  }, [search])

  if (pageState === 'loading') {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <Skeleton active />
        </Card>
      </div>
    )
  }

  if (pageState !== 'ready' || !profile) {
    const subtitle =
      pageState === 'invalidParameters'
        ? translate('customerServiceUserProfile.invalidParameters')
        : pageState === 'notFound'
          ? translate('customerServiceUserProfile.notFound')
          : translate('customerServiceUserProfile.loadError')

    return <Result status="error" subTitle={subtitle} />
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card} size="small">
        <div className={styles['section-title']}>
          {translate('customerServiceUserProfile.userOverview')}
        </div>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={translate('customerServiceUserProfile.userId')}>
            {profile.userId}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.name')}>
            {displayValue(profile.name)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.userType')}>
            {userTypeTag(profile.userType)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.gender')}>
            {gender(profile.gender)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.phone')}>
            {displayValue(profile.phone)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.city')}>
            {displayValue(profile.city)}
          </Descriptions.Item>
        </Descriptions>

        {profile.userType === 'CORPORATE_USER' && profile.corporate && (
          <>
            <div className={styles['section-title']}>
              {translate('customerServiceUserProfile.corporateInformation')}
            </div>
            <Descriptions column={2} size="small">
              <Descriptions.Item label={translate('customerServiceUserProfile.corporateSummary')}>
                {`${profile.corporate.id}-${profile.corporate.name}`}
              </Descriptions.Item>
              <Descriptions.Item label={translate('customerServiceUserProfile.corporateCredits')}>
                {profile.corporate.credits}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <div className={styles['section-title']}>
          {translate('customerServiceUserProfile.creditBalance')}
        </div>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={translate('customerServiceUserProfile.credits')}>
            {profile.credits}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.frozenCredits')}>
            {profile.frozenCredits}
          </Descriptions.Item>
          <Descriptions.Item
            label={translate('customerServiceUserProfile.personalCreditsExpiredAt')}
          >
            {profile.personalCreditsExpiredAt ? getDateTime(profile.personalCreditsExpiredAt) : '-'}
          </Descriptions.Item>
        </Descriptions>

        <div className={styles['section-title']}>
          {translate('customerServiceUserProfile.orderInfo')}
        </div>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={translate('customerServiceUserProfile.purchasedOrderCount')}>
            {profile.purchasedOrderCount}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.lastPurchaseAmount')}>
            {profile.lastPurchase ? `¥${profile.lastPurchase.amountInCents / 100}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={translate('customerServiceUserProfile.lastPurchaseAt')}>
            {profile.lastPurchase ? getDateTime(profile.lastPurchase.purchasedAt) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  )
}

export default CustomerServiceUserProfilePage
